import React, { useState } from 'react';
import {
  Video,
  Users,
  MessageCircle,
  Wrench,
  Calendar,
  Clock,
  Hourglass,
  User,
  Check,
  CalendarPlus,
  Download,
  Bell
} from 'lucide-react';

interface MentoringSession {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  duration: string;
  host: string;
  hostRole: string;
  attendees: number;
  maxAttendees: number;
  type: 'group' | 'qa' | 'workshop';
  zoomLink?: string;
}

interface MentoringCalendarProps {
  isAuthenticated: boolean;
  onLoginClick: () => void;
}

// Generate upcoming sessions dynamically
const generateUpcomingSessions = (): MentoringSession[] => {
  const now = new Date();
  const sessions: MentoringSession[] = [];

  // Next 3 monthly mentoring sessions (3rd Thursday of each month)
  for (let i = 0; i < 3; i++) {
    const sessionDate = new Date(now);
    sessionDate.setMonth(now.getMonth() + i);

    // Find 3rd Thursday
    sessionDate.setDate(1);
    let thursdayCount = 0;
    while (thursdayCount < 3) {
      if (sessionDate.getDay() === 4) thursdayCount++;
      if (thursdayCount < 3) sessionDate.setDate(sessionDate.getDate() + 1);
    }

    sessions.push({
      id: `monthly-${i}`,
      title: 'Monthly Coffee Business Mentoring',
      description: 'Open Q&A session with experienced coffee entrepreneurs. Bring your questions!',
      date: sessionDate.toISOString().split('T')[0],
      time: '7:00 PM AEDT',
      duration: '90 min',
      host: 'YourBeans Team',
      hostRole: 'Mentors',
      attendees: Math.floor(Math.random() * 15) + 5,
      maxAttendees: 30,
      type: 'group'
    });
  }

  // Add special workshops
  const workshopDate = new Date(now);
  workshopDate.setDate(workshopDate.getDate() + 14);
  sessions.push({
    id: 'workshop-branding',
    title: 'Workshop: Building Your Coffee Brand',
    description: 'Hands-on session covering logo design, packaging, and brand storytelling.',
    date: workshopDate.toISOString().split('T')[0],
    time: '6:00 PM AEDT',
    duration: '2 hours',
    host: 'Sarah Mitchell',
    hostRole: 'Brand Strategist',
    attendees: 12,
    maxAttendees: 20,
    type: 'workshop'
  });

  return sessions.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

const TYPE_ICONS = {
  group: Users,
  qa: MessageCircle,
  workshop: Wrench
};

const MentoringCalendar: React.FC<MentoringCalendarProps> = ({
  isAuthenticated,
  onLoginClick
}) => {
  const [sessions] = useState<MentoringSession[]>(() => generateUpcomingSessions());
  const [registeredSessions, setRegisteredSessions] = useState<string[]>([]);

  const isUpcoming = (dateStr: string) => {
    return new Date(dateStr) > new Date();
  };

  const handleRegister = (sessionId: string) => {
    if (!isAuthenticated) {
      onLoginClick();
      return;
    }
    setRegisteredSessions(prev =>
      prev.includes(sessionId)
        ? prev.filter(id => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  const getTypeColor = (type: MentoringSession['type']) => {
    switch (type) {
      case 'group': return 'bg-blue-100 text-blue-600';
      case 'qa': return 'bg-purple-100 text-purple-600';
      case 'workshop': return 'bg-green-100 text-green-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Mentoring Sessions</h2>
        <div className="flex items-center text-sm text-gray-500">
          <Video className="w-4 h-4 mr-1" />
          <span>Live on Zoom</span>
        </div>
      </div>

      {/* Feature Banner */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl p-4 text-white">
        <div className="flex items-start">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-bold mb-1">Free Monthly Mentoring</h3>
            <p className="text-blue-100 text-sm">
              Join live sessions with successful coffee business owners. Ask questions, get feedback, and connect with the community.
            </p>
          </div>
        </div>
      </div>

      {/* Sessions List */}
      <div className="space-y-3">
        {sessions.filter(s => isUpcoming(s.date)).map(session => {
          const isRegistered = registeredSessions.includes(session.id);
          const spotsLeft = session.maxAttendees - session.attendees;
          const TypeIcon = TYPE_ICONS[session.type] || Calendar;

          return (
            <div
              key={session.id}
              className="bg-white rounded-xl p-4 shadow-sm"
            >
              <div className="flex items-start">
                {/* Date Badge */}
                <div className="text-center mr-4 flex-shrink-0">
                  <div className="text-xs text-gray-500 uppercase">
                    {new Date(session.date).toLocaleDateString('en-AU', { month: 'short' })}
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {new Date(session.date).getDate()}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(session.date).toLocaleDateString('en-AU', { weekday: 'short' })}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium flex items-center ${getTypeColor(session.type)}`}>
                      <TypeIcon className="w-3 h-3 mr-1" />
                      {session.type === 'group' ? 'Group Session' : session.type === 'qa' ? 'Q&A' : 'Workshop'}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900">{session.title}</h3>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{session.description}</p>

                  <div className="flex flex-wrap items-center mt-2 text-xs text-gray-500 gap-x-3 gap-y-1">
                    <span className="flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {session.time}
                    </span>
                    <span className="flex items-center">
                      <Hourglass className="w-3 h-3 mr-1" />
                      {session.duration}
                    </span>
                    <span className="flex items-center">
                      <User className="w-3 h-3 mr-1" />
                      {session.host}
                    </span>
                  </div>

                  {/* Spots & Register */}
                  <div className="flex items-center justify-between mt-3">
                    <div className="text-xs">
                      <span className={spotsLeft <= 5 ? 'text-orange-600 font-medium' : 'text-gray-500'}>
                        {spotsLeft} spots left
                      </span>
                      <span className="text-gray-400 ml-1">
                        ({session.attendees} registered)
                      </span>
                    </div>
                    <button
                      onClick={() => handleRegister(session.id)}
                      className={`flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        isRegistered
                          ? 'bg-green-100 text-green-700'
                          : 'bg-amber-500 text-white hover:bg-amber-600'
                      }`}
                    >
                      {isRegistered ? (
                        <>
                          <Check className="w-4 h-4 mr-1" />
                          Registered
                        </>
                      ) : (
                        <>
                          <CalendarPlus className="w-4 h-4 mr-1" />
                          Register
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add to Calendar CTA */}
      {isAuthenticated && registeredSessions.length > 0 && (
        <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center">
            <Calendar className="w-5 h-5 text-gray-400 mr-3" />
            <span className="text-sm text-gray-600">
              Add {registeredSessions.length} session(s) to your calendar
            </span>
          </div>
          <button className="text-sm text-amber-600 font-medium flex items-center hover:underline">
            <Download className="w-4 h-4 mr-1" />
            Download .ics
          </button>
        </div>
      )}

      {/* Not Authenticated CTA */}
      {!isAuthenticated && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center">
            <Bell className="w-6 h-6 text-amber-500 mr-3" />
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900">Get Reminders</h4>
              <p className="text-sm text-gray-600">
                Sign up to register for sessions and receive reminders
              </p>
            </div>
            <button
              onClick={onLoginClick}
              className="bg-amber-500 text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-amber-600 transition-colors"
            >
              Sign Up
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MentoringCalendar;
